/* Session lives in an HTTP-only cookie; useMe() resolves to the current user
   or null (401 is "signed out", not an error). Apps branch on that at the
   root — everything below can assume a user exists. Sign-in and sign-out are
   browser transitions through the central provider, not query mutations. */

import { queryOptions, useQuery, type QueryKey } from "@tanstack/react-query";

import { NOT_PERSISTED, isTransientQueryError } from "./query-client";
import { ApiError } from "./request";

export function createAuthHooks<TUser>(api: {
  queryKey: QueryKey;
  me: () => Promise<TUser>;
}) {
  /* The session as *options*, not only as a hook.
   *
   * Identity is the one read everything else waits on, so the route that establishes it
   * has to be able to start it before a component exists — `ensureQueryData(sessionQuery)`
   * in a `beforeLoad`, then `useMe()` reading the answer out of the cache. Two callers,
   * one query, and no second key to keep in step. */
  const sessionQuery = queryOptions<TUser | null>({
    queryKey: api.queryKey,
    // Never from disk. Who is at the keyboard is the one answer a persisted cache must
    // not be allowed to give: a device is shared, and a snapshot that outlives the tab
    // it was written in would let the next person open the console as the last one.
    // Every other read below this identity can be warm; this one is always a door call.
    meta: NOT_PERSISTED,
    queryFn: async () => {
      try {
        return await api.me();
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    // A restart of the one API process leaves no door for a minute or two. Who is signed
    // in has not changed meanwhile, so ride it out (about 40 s of backoff) rather than tell
    // the person their sign-in could not be checked. A 401 is an answer, never retried.
    retry: (failures, error) => failures < 10 && isTransientQueryError(error),
    retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 5_000),
  });

  function useMe() {
    return useQuery(sessionQuery);
  }

  return { useMe, sessionQuery };
}
